import React from "react";
import { CheckCircle } from "lucide-react";

const Loan = () => {
  const benefits = [
    "Improve access to credit for farmers and rural households",
    "Streamline loan processing to reduce delays",
    "Promote transparency and reduce paperwork",
    "Support income-generating activities and livelihood development",
    "Strengthen cooperative credit delivery at the grassroots",
  ];

  const processSteps = [
    {
      step: "Application Submission",
      description:
        "Farmer or member applies through PACCS, DCCB or cooperative bank counters. Digital application options available in select societies.",
    },
    {
      step: "Eligibility Verification",
      description:
        "Land records, crop details, SHG membership, and repayment history verified.",
    },
    {
      step: "Loan Appraisal",
      description:
        "Society/bank verifies the requirement, credit limit, and repayment capacity.",
    },
    {
      step: "Approval and Sanction",
      description:
        "Loan is sanctioned based on norms. Member receives sanction intimation.",
    },
    {
      step: "Disbursal",
      description: "Amount transferred directly to the beneficiary’s bank account.",
    },
    {
      step: "Monitoring and Renewal",
      description:
        "Repayment tracked through MIS. Renewal/cycle support provided for recurring loans like KCC.",
    },
  ];

  const keyStats = [
    { label: "Total Loans Disbursed", value: "X crore" },
    { label: "Farmers Supported", value: "X lakh" },
    { label: "Women Beneficiaries", value: "X lakh" },
    { label: "Automated Loans Processed", value: "X" },
  ];

  return (
    <div className="py-20 px-6 bg-[#f4f8ff] ">
  <div className="w-screen bg-white py-10 shadow-sm -mt-20">
  <h1 className="text-center text-4xl font-bold text-gray-gray-900">
    Loan Mechanisation
  </h1>
</div>

      <div className="max-full mx-auto flex flex-col gap-16">

        {/* Overview */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Overview</h2>
          <div className="bg-blue-50 border-l-4 border-blue-600 rounded-lg p-6">
            <p className="text-gray-800 text-lg leading-relaxed">
              Loan Mechanisation enables farmers, women, self-help groups, and
              rural entrepreneurs to access timely and structured credit through
              cooperative institutions. The system simplifies the loan
              application, approval, and disbursal process using digital tools
              and standardised procedures. The goal is to make cooperative credit
              faster, transparent, and accessible to every eligible citizen.
            </p>
          </div>
        </section>

      {/* Benefits Boxes Styled Like Image */}
{/* Benefits Boxes Styled Like Image */}
<section>
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
    {benefits.map((benefit, index) => {
      const colors = [
        "bg-teal-500",     // Like Empathize
        "bg-blue-900",     // Like Define
        "bg-yellow-400",   // Like Ideate
        "bg-cyan-500",     // Like Prototype
        "bg-purple-700",   // Like Test
      ];

      const icons = ["🤝", "⚡", "📋", "🌱", "🏦"]; // Simple icons for each benefit

      return (
        <div
          key={index}
          className={`${colors[index]} text-white rounded-2xl p-8 flex flex-col items-center justify-center gap-4 shadow-lg min-h-[200px]`}
        >
          <p className="font-semibold text-center text-base leading-snug">
            {benefit}
          </p>
        </div>
      );
    })}
  </div>
</section>

     {/* Loan Mechanisation Process */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Loan Mechanisation Process
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {processSteps.map((step, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-white border-l-4 border-blue-600 shadow-md hover:shadow-xl transition-all duration-300 rounded-lg p-4 group">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg group-hover:scale-110 transition-transform">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-700 transition-colors">
                      {step.step}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
{/* Key Stats */}
<section className="bg-blue-200 py-10 rounded-xl w-full">
  <h2 className="text-3xl font-bold text-black mb-8 text-center">
    Key Stats
  </h2>

  <div className="w-full mx-auto px-4">
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {keyStats.map((stat, index) => (
        <div
          key={index}
          className="bg-white rounded-2xl shadow-sm p-6 flex items-center gap-4"
        >
          {/* Icon Circle */}
          <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 text-xl">
            {stat.icon}
          </div>

          {/* Text */}
          <div className="text-left">
            <div className="text-2xl font-bold text-gray-900">
              {stat.value}
            </div>
            <div className="text-sm text-gray-600">
              {stat.label}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
</section>

  
      
        {/* Features Section */}
        <section>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {/* Feature 1 */}
            <div className="bg-blue-100 rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow relative">
              <div className="absolute -top-6 left-6">
                <div className="w-14 h-14 bg-gray-800 rounded-full shadow-lg"></div>
              </div>
              <div className="mt-8">
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Simplified digital application
                </h3>
             
              </div>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow relative">
              <div className="absolute -top-6 left-6">
                <div className="w-14 h-14 bg-pink-500 rounded-full shadow-lg"></div>
              </div>
              <div className="mt-8">
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Faster verification and approval
                </h3>
             
              </div>
            </div>

            {/* Feature 3 */}
            <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow relative">
              <div className="absolute -top-6 left-6">
                <div className="w-14 h-14 bg-green-500 rounded-full shadow-lg"></div>
              </div>
              <div className="mt-8">
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Direct benefit transfer to eligible accounts
                </h3>
                
              </div>
            </div>

            {/* Feature 4 */}
            <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow relative">
              <div className="absolute -top-6 left-6">
                <div className="w-14 h-14 bg-blue-600 rounded-full shadow-lg"></div>
              </div>
              <div className="mt-8">
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Standardised interest rates under cooperative norms
                </h3>
            
              </div>
            </div>

            {/* Feature 5 */}
            <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow relative">
              <div className="absolute -top-6 left-6">
                <div className="w-14 h-14 bg-purple-600 rounded-full shadow-lg"></div>
              </div>
              <div className="mt-8">
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Timely renewal reminders and loan cycle management
                </h3>
         
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Loan;
